import Link from "next/link";

import { CSSObject } from "@emotion/react";
import { Anchor, Button } from "@newsela/angelou";
import { withTrackEvent } from "@newsela/snitch";

import { useNavigationBarV2Context } from "@/base/NewselaGlobalLayoutV2/NavigationBar";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import { trialButtonCss } from "./TrialButtons.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);
const AnchorWithTrackEvent = withTrackEvent(Anchor);

type ExperimentSlugs = "req_school" | "req_school_trial";

type ButtonExperiementType = {
  [key in ExperimentSlugs]: {
    copy: string;
    href: string;
  };
};

const TRIAL_BUTTON_EXPERIMENT_TEXT_MAP: ButtonExperiementType = {
  req_school: {
    copy: "Request school access",
    href: "https://newsela.com/request-upgrade",
  },
  req_school_trial: {
    copy: "Request school access",
    href: "https://go.newsela.com/REF-2023-04-POC-Request-Upgrade_03-Referral---Version-B.html",
  },
};

export const TrialButtons = ({ rootCss = {} }: ITrialButtons) => {
  const { data: user } = useUser();
  const {
    canStartPremiumTrial,
    isTeacher,
    isAuthorizedPrincipal,
    isAuthorizedDistrictAdmin,
    isLiteUser,
    hasActivePremiumTrial,
    hasPremiumLicense,
  } = getUserProperties(user, { shouldUseNewUserApi: false });

  const {
    trialModal: { trialModalRef, setIsTrialModalOpen },
  } = useNavigationBarV2Context();

  const [referralExperimentVariant] = useGetUserExperimentSegment(
    "referral_cta_text_3",
  );

  const isAdmin = isAuthorizedPrincipal || isAuthorizedDistrictAdmin;
  const canRequestSchoolTrial =
    (isTeacher || isAdmin) &&
    (isLiteUser || hasActivePremiumTrial) &&
    !hasPremiumLicense;
  const requestAccessTrialButtonDefinitions =
    TRIAL_BUTTON_EXPERIMENT_TEXT_MAP[
      referralExperimentVariant as ExperimentSlugs
    ] || TRIAL_BUTTON_EXPERIMENT_TEXT_MAP["req_school"];

  let button;

  if (canStartPremiumTrial) {
    button = (
      <ButtonWithTrackEvent
        objectType="User"
        actionPrefix="Initiate"
        actionName="Start"
        streamProperties={{ description: "Start free trial modal overlay." }}
        __cssFor={trialButtonCss}
        onClick={() => setIsTrialModalOpen(true)}
        // @ts-expect-error ref from angelou doesn't have an accessible type
        ref={trialModalRef}
        dataProps={{ "data-qa-selector": "global-nav-start-free-trial" }}
      >
        Start free trial
      </ButtonWithTrackEvent>
    );
  } else if (canRequestSchoolTrial) {
    button = (
      <Link
        href={requestAccessTrialButtonDefinitions.href}
        passHref
        legacyBehavior
      >
        <AnchorWithTrackEvent
          dataProps={{
            "data-testid": "requestSchoolAccess",
            "data-qa-selector": "global-nav-request-school-access",
          }}
          objectType="School"
          actionPrefix="Initiate"
          actionName="Start"
          streamProperties={{
            description: "Open school trial request page.",
          }}
          __cssFor={trialButtonCss}
          __renderCustomLink={({ children, href, ...anchorProps }) => {
            return (
              <Link href={href as URL} passHref legacyBehavior>
                <a {...anchorProps}>{children}</a>
              </Link>
            );
          }}
        >
          {requestAccessTrialButtonDefinitions.copy}
        </AnchorWithTrackEvent>
      </Link>
    );
  }

  if (button) {
    return <li css={rootCss}>{button}</li>;
  }

  return null;
};

interface ITrialButtons {
  rootCss?: CSSObject;
}
